<script setup lang="ts">
const site = useSite();
const { data } = await useKirbyData("home", {
  transform: (page: Record<string, any>) => {
    const slim = slimPagePayload(page) as Record<string, any>;
    // Homepage map is ClientOnly: load markers on demand via /api/map-data
    if (slim && typeof slim === "object") {
      const { mapData: _mapData, ...rest } = slim;
      return rest;
    }
    return slim;
  },
});
const { data: feefoData } = await useFetch("/api/feefo", {
  transform: (payload: Record<string, any>) => {
    if (!payload || typeof payload !== "object") return payload;
    // Keep rating summary for JSON-LD / badges; drop bulky review arrays on homepage
    const { reviews, ...rest } = payload as { reviews?: unknown };
    if (!Array.isArray(reviews)) return payload;
    return {
      ...rest,
      reviews: reviews.slice(0, 3),
    };
  },
});
const route = useRoute();
const { siteUrl } = useRuntimeConfig().public;

// Set the current page data for the global page context
setPage(data.value);

useJsonLdHead(
  computed(() =>
    data.value
      ? buildHomePageGraphJsonLd(
          data.value,
          site.value,
          route.path,
          siteUrl,
          feefoData.value,
        )
      : null,
  ),
);

useHead({
  bodyAttrs: {
    class: `${data.value?.template}-page`,
  },
});

definePageMeta({
  layout: "default",
});
</script>

<template>
  <div>
    <AppHeading1 />
    <div id="homeHero">
      <AppHeroSliderVideo v-if="data?.heroType === 'Hero Slider Video'" />
    </div>
    <section id="homeIntro">
      <div class="intro-text">
        <KirbyLayouts :layouts="data?.layouts ?? []" />
      </div>
      <div id="feefoAward">
        <NuxtPicture
          class="feefo-award-desktop"
          format="webp"
          fit="inside"
          loading="lazy"
          :src="site?.feefo?.url"
          :width="site?.feefo?.width"
          :height="site?.feefo?.height"
          :alt="site?.feefo?.alt || 'Feefo Trusted Service Award'"
          sizes="sm:150px md:180px lg:250px"
        />
        <NuxtPicture
          class="feefo-award-mobile"
          format="webp"
          fit="inside"
          loading="lazy"
          :src="site?.feefoMobile?.url"
          :width="site?.feefoMobile?.width"
          :height="site?.feefoMobile?.height"
          :alt="site?.feefoMobile?.alt || 'Feefo Trusted Service Award'"
          sizes="sm:150px md:180px lg:250px"
        />
      </div>
    </section>
    <div class="content-wrapper">
      <section id="trekNavEurope">
        <div class="container">
          <header>
            <div>
              <h2>Treks in Europe</h2>
              <h3>Our Classic Treks Routes</h3>
            </div>
          </header>
          <AppTrekNavEurope />
        </div>
      </section>
      <AppQuizGrading />
      <section id="trekNavWorldwide">
        <div class="container">
          <header>
            <AppLogoWWTWide />
            <div>
              <h2>The Best of the Rest of the World</h2>
              <h3>
                Looking for adventures beyond the Alps? Discover our WorldWide
                trekking tours.
              </h3>
            </div>
          </header>
          <AppTrekNavWorldwide />
        </div>
      </section>
      <section id="mapboxMap">
        <AppMapsMapboxShell />
      </section>
      <section id="aboutMBT">
        <h2>About Mont Blanc Treks</h2>
        <div class="container">
          <div class="about-wrap">
            <div class="about-box">
              <AppSVGKnowledgeIcon />
              <p>
                Mont Blanc Treks is run by a small, dedicated team of
                like-minded people whose local knowledge and passion for the
                mountains inspires us to offer a more personalised service for
                all our trekking holidays.
              </p>
            </div>
            <div class="about-box">
              <AppSVGGuidesIcon />
              <p>
                All our guided alpine treks are led by qualified UIMLA mountain
                professionals who ensure your safety at all times as well as
                offering you a wealth of information regarding the flora, fauna
                and history of the area.
              </p>
            </div>
            <div class="about-box">
              <AppSVGFeefoIcon />
              <p>
                We are proud to have been awarded the Feefo Trusted Service
                Award for 6 years running!
              </p>
              <NuxtLink to="/reviews" class="btn btn-small">
                Read Our Reviews
              </NuxtLink>
            </div>
          </div>
        </div>
      </section>
      <section id="featuredArticles">
        <div class="container">
          <AppFeaturedArticles worldwide="false" />
        </div>
      </section>
    </div>
  </div>
</template>
